/*
 * Supabase SQL — run once in the Supabase SQL editor before using this form:
 *
 * CREATE TABLE vendor_applications (
 *   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
 *   name text NOT NULL,
 *   business_name text NOT NULL,
 *   email text NOT NULL,
 *   phone text,
 *   city_state text NOT NULL,
 *   description text NOT NULL,
 *   meals_per_week text NOT NULL,
 *   has_license text NOT NULL,
 *   status text DEFAULT 'pending',
 *   created_at timestamptz DEFAULT now()
 * );
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { submitVendorApplication } from "../lib/api";

const MEALS_OPTIONS = [
  { value: "<50", label: "Less than 50" },
  { value: "50-100", label: "50–100" },
  { value: "100-200", label: "100–200" },
  { value: "200+", label: "200+" },
];

const LICENSE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "in_progress", label: "In progress" },
];

const EMPTY_FORM = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  city_state: "",
  description: "",
  meals_per_week: "",
  has_license: "",
};

export default function VendorApply() {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!agreed) return;
    setSubmitting(true);
    try {
      await submitVendorApplication(form);
      setSubmittedEmail(form.email);
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Submission failed", description: detail });
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-lg px-4 py-20 text-center">
          <div className="text-4xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold mb-3">Application received!</h1>
          <p className="text-neutral-600 text-sm leading-relaxed">
            We'll be in touch at <strong>{submittedEmail}</strong> within 2 business days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-2">Become a PrepMate vendor</h1>
        <p className="text-neutral-600 mb-8">
          Tell us about your business and we'll be in touch within 2 business days.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Full name */}
          <div>
            <label className="text-sm font-medium block mb-1">
              Full name <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder="Jane Smith"
              required
              className="bg-white"
            />
          </div>

          {/* Business name */}
          <div>
            <label className="text-sm font-medium block mb-1">
              Business name <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.business_name}
              onChange={set("business_name")}
              placeholder="ATX Fresh Prep"
              required
              className="bg-white"
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium block mb-1">
              Email address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="jane@example.com"
              required
              className="bg-white"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="text-sm font-medium block mb-1">Phone number</label>
            <Input
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="(512) 555-0100"
              className="bg-white"
            />
          </div>

          {/* City and state */}
          <div>
            <label className="text-sm font-medium block mb-1">
              City and state <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.city_state}
              onChange={set("city_state")}
              placeholder="Austin, TX"
              required
              className="bg-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium block mb-1">
              Brief description of your meal prep business <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={form.description}
              onChange={set("description")}
              placeholder="Tell us what you make, who you serve, and what makes your food special."
              required
              rows={4}
              className="bg-white"
            />
          </div>

          {/* Meals per week */}
          <div>
            <label className="text-sm font-medium block mb-1">
              How many meals do you prepare per week? <span className="text-red-500">*</span>
            </label>
            <select
              value={form.meals_per_week}
              onChange={set("meals_per_week")}
              required
              className="w-full border rounded-md p-2 bg-white text-sm"
            >
              <option value="" disabled>Select an option</option>
              {MEALS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Food handler license */}
          <div>
            <label className="text-sm font-medium block mb-2">
              Do you hold a valid food handler license? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-6">
              {LICENSE_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="has_license"
                    value={o.value}
                    checked={form.has_license === o.value}
                    onChange={set("has_license")}
                    required
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          {/* Agreement checkbox */}
          <label className="flex items-start gap-2 text-sm text-neutral-600 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 shrink-0"
              required
            />
            <span>
              I agree to the{" "}
              <Link
                to="/vendor-agreement"
                className="underline hover:text-black"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vendor Agreement
              </Link>{" "}
              and understand that PrepMate will review my application.
            </span>
          </label>

          <Button
            type="submit"
            disabled={submitting || !agreed}
            className="w-full bg-[#2a7d4f] text-white hover:bg-[#236840] h-11"
          >
            {submitting ? "Submitting..." : "Submit application"}
          </Button>
        </form>
      </div>
    </div>
  );
}
